import * as yup from "yup";
import React from "react";
import i18n, { useTranslation } from "utilities/i18n";
import { useUpdateServiceNowWebhookMutation } from "generated/graphql";
import { AutoSave, Form, Formik, Input } from "collections";
import { SubForms } from "./SubForm";
import { ChangeSaveNotification } from "./ChangeSaveNotification";
import { InferType } from "yup";

export const NameToggleFormSchema = yup.object({
  name: yup
    .string()
    .label(i18n.t("NAME"))
    .default("")
    .test("Name required", i18n.t("NAME_IS_REQUIRED"), function(name) {
      return !(name.length === 0);
    })
    .test(
      "Duplicate Webhook error",
      i18n.t("ERROR_DUPLICATE_INTEGRATION_HOOK"),
      function(name) {
        const { hooks, id } = this.parent;

        if (name.length) {
          const foundHook = !!hooks.find(
            (x: { name: string; id: string }) =>
              x.name.toUpperCase() === name.toUpperCase() && x.id !== id
          );
          return !foundHook;
        } else {
          return true;
        }
      }
    )
    .required(),
  url: yup
    .string()
    .url()
    .test("Invalid URL path", i18n.t("URL_IS_REQUIRED"), function(url) {
      return !(url.length === 0);
    })
    .test("Invalid URL path", i18n.t("ERROR_INVALID_URL_FORMAT"), function(
      url
    ) {
      if (url.length) {
        return url.endsWith("/api/global/em/jsonv2");
      }
    })
    .label(i18n.t("URL"))
    .default("")
    .required(),
  username: yup
    .string()
    .label(i18n.t("USERNAME"))
    .required(),
  password: yup.string().label("PASSWORD"),
  id: yup.string(),
  hooks: yup
    .array()
    .of(yup.object())
    .default([])
});
export type EditType = InferType<typeof NameToggleFormSchema>;

export const NameForm: React.FC<SubForms> = ({
  webHooksId,
  ac: { name, url, username },
  hooks
}) => {
  const { t } = useTranslation();
  const [, updateHook] = useUpdateServiceNowWebhookMutation();

  return (
    <Formik<EditType>
      key={webHooksId}
      name="ServiceNowUrlForm"
      validationSchema={NameToggleFormSchema}
      initialValues={{
        url,
        name,
        username,
        password: "",
        hooks: hooks || [],
        id: webHooksId
      }}
      onSubmit={async (values, helpers) => {
        let updateObject =
          values.password === "" || undefined
            ? {
                name: values.name,
                url: values.url,
                username: values.username
              }
            : {
                name: values.name,
                url: values.url,
                username: values.username,
                password: values.password
              };

        await updateHook({
          id: webHooksId,
          input: updateObject
        });
      }}
    >
      {({ isValid, field: { name, url, username, password } }) => (
        <Form className="form grid" data-testid="name-form">
          <ChangeSaveNotification />
          <AutoSave debounceMs={500} />
          <div className="mt-15 ph-2">
            <label>{t("NOTIFICATION_SERVICENOW_EDIT")}</label>
          </div>
          <div
            className={`form-group${isValid ? " error-message-spacing" : ""}`}
          >
            <label className="col-3 text-right" htmlFor={name.name}>
              {t("NAME")}:
            </label>
            <div className="col-5">
              <Input {...name} label={undefined} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-3 text-right" htmlFor={url.name}>
              {t("URL")}:
            </label>
            <div className="col-5">
              <Input
                {...url}
                label={undefined}
                placeholder={t("SERVICE_NOW_URL_EXAMPLE")}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-3 text-right" htmlFor={username.name}>
              {t("USERNAME")}:
            </label>
            <div className="col-5">
              <Input
                {...username}
                label={undefined}
                placeholder={t("SERVICE_NOW_USERNAME")}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-3 text-right" htmlFor={password.name}>
              {t("PASSWORD")}:
            </label>
            <div className="col-5">
              <Input
                {...password}
                label={undefined}
                placeholder={t("SERVICE_NOW_PASSWORD")}
                type="password"
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
